import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Button, Popup, Scheduler } from 'devextreme-react'
import ContextMenu, { ContextMenuRef } from 'devextreme-react/context-menu'
import {
  Resource,
  SchedulerRef,
  SchedulerTypes,
  Toolbar as SchedulerToolbar,
  ToolbarItem as SchedulerToolbarItem,
  View as SchedulerView,
} from 'devextreme-react/scheduler'
import CustomStore from 'devextreme/data/custom_store'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { calendarPageRouteLoader } from '@/routes/calendar/CalendarPage.route'
import { useLoaderData } from 'react-router-typesafe'
import { useScreenSize } from '@/themes/media-query'
import '../calendar/CalendarPage.scss'
import { useCalendarStore } from '@/routes/calendar/CalendarPage.store'
import { confirm } from 'devextreme/ui/dialog'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'
import { PopupRef } from 'devextreme-react/cjs/popup'
import { isUserRoleAllowed } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import Drawer, { DrawerRef } from 'devextreme-react/drawer'
import { CalendarRef as DxCalendarRef } from 'devextreme-react/calendar'
import { Item as ToolbarItem, Toolbar } from 'devextreme-react/toolbar'
import { DropDownButton } from 'devextreme-react/drop-down-button'
import type { ItemClickEvent } from 'devextreme/ui/context_menu'
import dxCalendar from 'devextreme/ui/calendar'
import CalendarPageDrawerContent from '@/routes/calendar/CalendarPage.drawer'
import {
  ContextMenuData,
  ExtendedAppointment,
  popupStateType,
  RecurringAction,
  RecurringScope,
  SchedulerViewType,
} from '@/routes/calendar/CalendarPage.types'
import {
  buildAppointmentMoveConfirm,
  buildCalendarFormData,
  buildEditData,
  canManageAppointment,
  expandAttendeesToPayload,
  normalizeAppointmentDateToSlot,
  removeById,
} from '@/routes/calendar/CalendarPage.utils'
import AppointmentPopupForm, { type AppointmentPopupFormRef } from '@/routes/calendar/CalendarPage.appointmentForm'
import RecurringScopeDialog from '@/routes/calendar/CalendarPage.recurringScopeDialog'
import { useAutoRefresh, useCalendarColors, useCalendarTree } from '@/routes/calendar/CalendarPage.hooks'
import { useAppointmentComponents } from '@/routes/calendar/CalendarPage.appointmentCell'

// Conversioni rapide da menu contestuale: nuovo oggetto, note svuotate e disponibilità impostata
const APPOINTMENT_CONVERSIONS = [
  { id: 'convert-phone-support', text: 'Assistenza Telefonica', icon: 'tel', status: 'Busy' },
  { id: 'convert-placeholder', text: 'Segnaposto', icon: 'pin', status: 'Free' },
] as const

const getDefaultAppointmentRangeForDate = (date: Date, now = new Date()) => {
  const isToday =
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()

  const startHour = isToday ? (now.getMinutes() > 0 ? now.getHours() + 1 : now.getHours()) : 8
  const startMinute = isToday ? 0 : 30
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), startHour, startMinute, 0)

  return {
    startDate,
    endDate: new Date(startDate.getTime() + 30 * 60 * 1000),
  }
}

const CalendarPage = () => {
  const { users, userInfo, calendarsGrouped, rooms } = useLoaderData<typeof calendarPageRouteLoader>()
  const currentScreenSize = useScreenSize()
  const { client } = useQsAdminApiManager()
  const calendarStore = useCalendarStore()

  const popupRef = useRef<PopupRef>(null)
  const schedulerRef = useRef<SchedulerRef>(null)
  const appointmentFormRef = useRef<AppointmentPopupFormRef>(null)
  const drawerRef = useRef<DrawerRef>(null)
  const pendingShowRef = useRef(false)
  const contextMenuRef = useRef<ContextMenuRef>(null)
  const contextMenuDataRef = useRef<ContextMenuData | null>(null)
  const pendingItemsRef = useRef<ExtendedAppointment[] | null>(null)
  const calendarLoadCacheRef = useRef<{ key: string; items: ExtendedAppointment[] } | null>(null)
  const forceNextCalendarLoadRef = useRef(false)
  const selectedMonthDateRef = useRef<Date | null>(null)
  const lastMonthCellClickRef = useRef<{ dayTime: number; clickedAt: number } | null>(null)
  const miniCalendarRef = useRef<DxCalendarRef>(null)
  const appointmentSaveButtonRef = useRef<{ option: (name: string, value?: unknown) => unknown } | null>(null)
  const appointmentSaveDisabledRef = useRef(false)
  // onAppointmentUpdating scatta sia per il trascinamento sia per il ridimensionamento: questo flag,
  // valorizzato in appointmentDragging.onDragEnd, distingue i due casi (solo il drag chiede conferma)
  const appointmentDragEndRef = useRef(false)

  const initState: popupStateType = {
    editData: {},
    creating: false,
  }

  const currentDate = new Date()

  const reducer = (state: popupStateType, action: popupStateType) => {
    return { ...state, ...action }
  }
  const [state, dispatch] = useReducer(reducer, initState)

  const setAppointmentSaveDisabled = useCallback((disabled: boolean) => {
    appointmentSaveDisabledRef.current = disabled
    appointmentSaveButtonRef.current?.option('disabled', disabled)
  }, [])

  const [scopeDialog, setScopeDialog] = useState<{
    open: boolean
    action: RecurringAction
    onSelect?: (scope: RecurringScope | null) => void
  }>({ open: false, action: 'edit' })
  const [showAppointmentLabels, setShowAppointmentLabels] = useState(
    () => localStorage.getItem('calendar-show-appointment-labels') !== 'false',
  )

  const loadWithPending = useCallback((pendingItems: ExtendedAppointment[]) => {
    pendingItemsRef.current = pendingItems
    schedulerRef.current?.instance().getDataSource().load()
  }, [])

  const reloadCalendarFromServer = useCallback(() => {
    forceNextCalendarLoadRef.current = true
    schedulerRef.current?.instance().getDataSource().reload()
  }, [])

  const isScopedRecurringAppointment = useCallback((appointment?: Partial<ExtendedAppointment>) => {
    return !!(
      appointment &&
      (appointment.recurrenceType === 'SeriesMaster' ||
        (appointment.seriesMasterId &&
          appointment.seriesMasterId !== appointment.appointmentId &&
          (appointment.recurrenceType === 'Occurrence' || appointment.recurrenceType === 'Exception')))
    )
  }, [])

  const chooseRecurringScope = useCallback(
    (appointment: ExtendedAppointment, action: RecurringAction) =>
      new Promise<RecurringScope | null>((resolve) => {
        if (!isScopedRecurringAppointment(appointment)) {
          resolve('appointment')
          return
        }

        setScopeDialog({
          open: true,
          action,
          onSelect: (scope) => {
            setScopeDialog({ open: false, action })
            resolve(scope)
          },
        })
      }),
    [isScopedRecurringAppointment],
  )

  const openEditor = useCallback(
    (appointmentData: Partial<ExtendedAppointment>, creating: boolean) => {
      dispatch({
        editData: buildEditData(appointmentData, {
          email:
            appointmentData?.email ??
            (isUserRoleAllowed(userInfo?.roles, [Roles.Supervisor, Roles.Administrator, Roles.Organizer])
              ? undefined
              : userInfo?.email),
        }),
        creating,
      })
      pendingShowRef.current = true
    },
    [userInfo?.email, userInfo?.roles],
  )

  const resolveSeriesEditAppointment = useCallback(
    async (appointment: ExtendedAppointment) => {
      const response = await client.post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/recurrence-info`, {
        appointmentId: appointment.appointmentId,
        email: appointment.email,
        scope: 'series',
      })

      const recurrenceRule = response.data.data?.recurrenceRule ?? ''
      return {
        ...(response.data.data?.targetEvent ?? appointment),
        recurrenceRule,
      } as ExtendedAppointment
    },
    [client],
  )

  const requestEditAppointment = useCallback(
    async (appointment: ExtendedAppointment) => {
      const scope = await chooseRecurringScope(appointment, 'edit')
      if (!scope) return

      const targetAppointment = scope === 'series' ? await resolveSeriesEditAppointment(appointment) : appointment
      openEditor(targetAppointment, false)
    },
    [chooseRecurringScope, openEditor, resolveSeriesEditAppointment],
  )

  const requestDeleteAppointment = useCallback(
    async (appointment: ExtendedAppointment) => {
      const scope = await chooseRecurringScope(appointment, 'delete')
      if (!scope) return

      const targetAppointment =
        scope === 'series' && appointment.seriesMasterId
          ? { ...appointment, appointmentId: appointment.seriesMasterId }
          : appointment

      const result = confirm(
        `<i>Vuoi davvero eliminare l'evento <b>${appointment.text}</b>${scope === 'series' ? ' e tutta la serie' : ''}?</i>`,
        'Eliminazione evento',
      )
      result.then(async (dialogResult) => {
        if (dialogResult === false) return
        try {
          await schedulerRef.current
            ?.instance()
            .getDataSource()
            .store()
            .remove({ ...targetAppointment })
          const currentItems = schedulerRef.current?.instance().getDataSource().items() ?? []
          loadWithPending(
            scope === 'series' && appointment.seriesMasterId
              ? currentItems.filter(
                  (item) =>
                    item.appointmentId !== appointment.seriesMasterId &&
                    item.seriesMasterId !== appointment.seriesMasterId,
                )
              : removeById(currentItems, appointment.appointmentId),
          )
        } catch (error) {
          notify(`Errore eliminazione evento: ${error}`, 'error', 2000)
        }
      })
    },
    [chooseRecurringScope, loadWithPending],
  )

  const update = useCallback(
    (
      key: string | ExtendedAppointment,
      values: Partial<ExtendedAppointment> & { newAttachments?: File[]; removedAttachmentIds?: string[] },
    ) => {
      const keyId = typeof key === 'string' ? key : (key as ExtendedAppointment).appointmentId
      const existingItem = (schedulerRef.current?.instance().getDataSource().items() ?? []).find(
        (i: ExtendedAppointment) => i.appointmentId === keyId,
      ) as ExtendedAppointment | undefined
      const shouldNormalizeSchedulerUpdate = typeof key !== 'string' && !values.allDay
      const startDateRaw: Date = new Date(values.startDate ?? '')
      const endDateRaw: Date = new Date(values.endDate ?? '')
      const startDate = shouldNormalizeSchedulerUpdate ? normalizeAppointmentDateToSlot(startDateRaw) : startDateRaw
      const endDate = shouldNormalizeSchedulerUpdate ? normalizeAppointmentDateToSlot(endDateRaw) : endDateRaw
      const updateValues = {
        ...values,
        email: values.email ?? existingItem?.email,
        startDate,
        endDate,
        appointmentId: keyId,
        isOnlineMeeting: values.isOnlineMeeting ?? existingItem?.isOnlineMeeting ?? false,
      }
      const formData = buildCalendarFormData({
        values: {
          ...updateValues,
          requiredAttendees: expandAttendeesToPayload(
            updateValues.requiredAttendees ?? existingItem?.requiredAttendees,
            users,
          ),
          optionalAttendees: expandAttendeesToPayload(
            updateValues.optionalAttendees ?? existingItem?.optionalAttendees,
            users,
          ),
        },
        newAttachments: values.newAttachments,
        removedAttachmentIds: values.removedAttachmentIds,
      })
      return client
        .put(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/update`, formData)
        .then((response) => {
          notify(`Evento modificato con successo.`, 'success', 2000)
          const currentItems = schedulerRef.current?.instance().getDataSource().items() ?? []
          const {
            newAttachments: _newAttachments,
            removedAttachmentIds: _removedAttachmentIds,
            ...persistedValues
          } = updateValues
          const hasMatchingItem = currentItems.some((item: ExtendedAppointment) => item.appointmentId === keyId)
          if (!hasMatchingItem) {
            pendingItemsRef.current = null
            return response
          }

          pendingItemsRef.current = currentItems.map((item: ExtendedAppointment) => {
            if (item.appointmentId !== keyId) return item

            const nextRecurrenceType =
              item.recurrenceType === 'Occurrence'
                ? 'Exception'
                : ((values.recurrenceType ?? item.recurrenceType) as string | undefined)

            return {
              ...item,
              ...persistedValues,
              startDate,
              endDate,
              recurrenceType: nextRecurrenceType,
              seriesMasterId: values.seriesMasterId ?? item.seriesMasterId,
            }
          })
          return response
        })
        .catch((error) => {
          notify(`Errore modifica evento: ${error}`, 'error', 2000)
        })
    },
    [client, users],
  )
  const insert = useCallback(
    (values: Partial<ExtendedAppointment> & { newAttachments?: File[]; removedAttachmentIds?: string[] }) => {
      const startDate: Date = new Date(values.startDate ?? '')
      const endDate: Date = new Date(values.endDate ?? '')
      const formData = buildCalendarFormData({
        values: {
          ...values,
          startDate,
          endDate,
        },
        newAttachments: values.newAttachments,
      })
      return client
        .post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/create`, formData)
        .then((response) => {
          notify(`Evento creato con successo.`, 'success', 2000)
          return response
        })
        .catch((error) => {
          notify(`Errore creazione evento: ${error}`, 'error', 2000)
          return error
        })
    },
    [client],
  )
  const remove = useCallback(
    (key: ExtendedAppointment) => {
      return client
        .delete(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/delete`, {
          data: key,
        })
        .then(() => {
          notify(`Evento eliminato con successo.`, 'success', 2000)
        })
        .catch((error) => {
          notify(`Errore eliminazione evento: ${error}`, 'error', 2000)
        })
    },
    [client],
  )

  // Reload esplicito quando cambia la selezione utenti (evita di ricreare il CustomStore)
  useEffect(() => {
    reloadCalendarFromServer()
  }, [calendarStore.selectedUserIds, reloadCalendarFromServer])

  //impostazione del numero della settimana sul calendario dello scheduler
  useEffect(() => {
    const applyWeekNumbersToSchedulerNavigatorCalendar = () => {
      document.querySelectorAll('.dx-scheduler-navigator-calendar').forEach((element) => {
        const calendarInstance = dxCalendar.getInstance(element) as InstanceType<typeof dxCalendar> | undefined
        if (!calendarInstance) return
        calendarInstance.option({
          showWeekNumbers: true,
          weekNumberRule: 'firstFourDays',
        })
      })
    }

    applyWeekNumbersToSchedulerNavigatorCalendar()

    const observer = new MutationObserver(() => {
      applyWeekNumbersToSchedulerNavigatorCalendar()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])

  const AUTO_REFRESH_OPTIONS = [
    { id: 0, text: 'Disattivato' },
    { id: 5, text: '5 minuti' },
    { id: 10, text: '10 minuti' },
    { id: 15, text: '15 minuti' },
    { id: 30, text: '30 minuti' },
  ]

  const { autoRefreshMinutes, handleAutoRefreshChange } = useAutoRefresh(reloadCalendarFromServer)

  const { colorOverrides } = useCalendarStore()

  const { usersWithColors } = useCalendarColors(users)

  const { displayedFlatCalendars, handleGroupCreate, handleGroupRename, handleDeleteGroup, handleTreeReorder } =
    useCalendarTree(calendarsGrouped)

  const displayedFlatCalendarsColored = useMemo(
    () =>
      displayedFlatCalendars.map((node) => ({
        ...node,
        color: node.email ? (colorOverrides[node.email] ?? node.color) : node.color,
      })),
    [displayedFlatCalendars, colorOverrides],
  )

  // Sincronizza l'ordine di selectedUserIds con l'ordine corrente del TreeList
  useEffect(() => {
    const currentIds = useCalendarStore.getState().selectedUserIds
    if (!currentIds.length) return
    const ordered = displayedFlatCalendars
      .filter((n) => n.email !== null && currentIds.includes(n.email!))
      .map((n) => n.email!)
    if (ordered.join(',') !== currentIds.join(',')) {
      calendarStore.setSelectedUserIds(ordered)
    }
  }, [displayedFlatCalendars])

  const schedulerDataSource = useMemo(
    () =>
      new CustomStore({
        key: 'appointmentId',
        load: (loadOptions) => {
          const filterRequest = {
            filter: loadOptions.filter, // Default filter from Scheduler
            users: useCalendarStore.getState().selectedUserIds, // Lettura live dal store senza dipendenza
          }
          const loadKey = JSON.stringify({
            filter: loadOptions.filter,
            startDate: loadOptions.startDate,
            endDate: loadOptions.endDate,
            users: filterRequest.users,
          })

          if (pendingItemsRef.current !== null) {
            const items = pendingItemsRef.current
            pendingItemsRef.current = null
            forceNextCalendarLoadRef.current = false
            calendarLoadCacheRef.current = { key: loadKey, items }
            return Promise.resolve(items)
          }

          if (!forceNextCalendarLoadRef.current && calendarLoadCacheRef.current?.key === loadKey) {
            return Promise.resolve(calendarLoadCacheRef.current.items)
          }

          forceNextCalendarLoadRef.current = false

          return client
            .post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/load`, filterRequest)
            .then((response) => {
              const appointments = response.data.data
              calendarLoadCacheRef.current = { key: loadKey, items: appointments }
              return appointments
            })
            .catch((error) => {
              notify(`Errore caricamento eventi: ${error}`, 'error', 2000)
            })
        },
        insert,
        update,
        remove,
      }),
    [client, insert, update, remove],
  )

  const { Appointment, AppointmentTooltip } = useAppointmentComponents({
    usersWithColors,
    userInfo,
    schedulerRef,
    dispatch,
    pendingShowRef,
    loadWithPending,
    requestDeleteAppointment,
    requestEditAppointment,
    currentSchedulerView: calendarStore.currentSchedulerView,
    showAppointmentLabels,
  })

  const onAppointmentFormOpening = useCallback(
    (e: SchedulerTypes.AppointmentFormOpeningEvent) => {
      e.cancel = true
      const creating = e.appointmentData && 'appointmentId' in e.appointmentData ? false : true
      if (creating) {
        const appointmentData = (e.appointmentData ?? {}) as Partial<ExtendedAppointment>
        if (calendarStore.currentSchedulerView === 'month' && appointmentData.startDate) {
          openEditor(
            {
              ...appointmentData,
              ...getDefaultAppointmentRangeForDate(new Date(appointmentData.startDate)),
              allDay: false,
            },
            true,
          )
          return
        }

        openEditor(appointmentData, true)
        return
      }
      requestEditAppointment(e.appointmentData as ExtendedAppointment)
    },
    [calendarStore.currentSchedulerView, openEditor, requestEditAppointment],
  )

  const onNewAppointmentButtonClick = useCallback(() => {
    const instance = schedulerRef.current?.instance()
    const currentDate: Date = (instance?.option('currentDate') as Date) ?? new Date()
    const { startDate, endDate } = getDefaultAppointmentRangeForDate(currentDate)

    openEditor(
      {
        startDate,
        endDate,
        email: isUserRoleAllowed(userInfo?.roles, [Roles.Supervisor, Roles.Administrator, Roles.Organizer])
          ? undefined
          : userInfo?.email,
      },
      true,
    )
  }, [openEditor, userInfo?.email, userInfo?.roles])

  useLayoutEffect(() => {
    if (pendingShowRef.current) {
      pendingShowRef.current = false
      popupRef.current?.instance().show()
    }
  }, [state])

  // ── AppointmentPopupForm wrapper ─────────────────────────────────────────
  const appointmentEditUser = users.find((item) => item.email === (state.editData.email ?? userInfo?.email))
  const appointmentEditEnabled = canManageAppointment(
    userInfo?.roles,
    userInfo?.email,
    appointmentEditUser?.email,
    state.editData.isOrganizer,
  )

  const appointmentPopupToolbarItems = useMemo(
    () => [
      {
        toolbar: 'top' as const,
        location: 'before' as const,
        widget: 'dxButton' as const,
        visible: appointmentEditEnabled,
        options: {
          icon: 'save',
          text: 'Salva',
          width: 110,
          stylingMode: 'contained' as const,
          type: 'success' as const,
          disabled: appointmentSaveDisabledRef.current,
          onInitialized: (e: { component?: { option: (name: string, value?: unknown) => unknown } }) => {
            appointmentSaveButtonRef.current = e.component ?? null
            appointmentSaveButtonRef.current?.option('disabled', appointmentSaveDisabledRef.current)
          },
          onDisposing: () => {
            appointmentSaveButtonRef.current = null
          },
          onClick: () => {
            appointmentFormRef.current?.save()
          },
        },
      },
    ],
    [appointmentEditEnabled],
  )

  const appointmentPopupContent = useCallback(
    () => (
      <AppointmentPopupForm
        saveRef={appointmentFormRef}
        state={state}
        users={users}
        userInfo={userInfo}
        rooms={rooms}
        schedulerRef={schedulerRef}
        popupRef={popupRef}
        loadWithPending={loadWithPending}
        client={client}
        currentScreenSize={currentScreenSize}
        onSaveDisabledChange={setAppointmentSaveDisabled}
      />
    ),
    [state, users, userInfo, rooms, loadWithPending, client, currentScreenSize, setAppointmentSaveDisabled],
  )

  const onAppointmentUpdating = useCallback(
    (e: SchedulerTypes.AppointmentUpdatingEvent) => {
      const isDragUpdate = appointmentDragEndRef.current
      appointmentDragEndRef.current = false

      if (!canManageAppointment(userInfo?.roles, userInfo?.email, e.newData.email, e.newData.isOrganizer)) {
        e.cancel = true
        return
      }

      if (!e.newData.allDay) {
        if (e.newData.startDate) {
          e.newData.startDate = normalizeAppointmentDateToSlot(new Date(e.newData.startDate))
        }
        if (e.newData.endDate) {
          e.newData.endDate = normalizeAppointmentDateToSlot(new Date(e.newData.endDate))
        }
      }

      // Conferma solo per lo spostamento: il ridimensionamento resta immediato e il salvataggio
      // da form non passa da questo evento
      if (!isDragUpdate) return

      const moveConfirm = buildAppointmentMoveConfirm(e.oldData, e.newData)
      if (!moveConfirm) return

      e.cancel = new Promise<boolean>((resolve) => {
        confirm(moveConfirm.messageHtml, moveConfirm.title).then(
          (dialogResult) => resolve(dialogResult === false),
          () => resolve(true),
        )
      })
    },
    [userInfo?.email, userInfo?.roles],
  )

  const onAppointmentDeleting = useCallback(
    (e: SchedulerTypes.AppointmentDeletingEvent) => {
      e.cancel = true // Blocca sempre la default deletion (passerebbe solo la key al remove)
      const canDelete = canManageAppointment(
        userInfo?.roles,
        userInfo?.email,
        e.appointmentData.email,
        e.appointmentData.isOrganizer,
      )
      if (!canDelete) return
      requestDeleteAppointment(e.appointmentData)
    },
    [requestDeleteAppointment, userInfo?.email, userInfo?.roles],
  )

  const appointmentDragging = useMemo(() => {
    return {
      onDragEnd: async (e: SchedulerTypes.AppointmentDraggingEndEvent) => {
        if (!canManageAppointment(userInfo?.roles, userInfo?.email, e.itemData.email, e.itemData.isOrganizer)) {
          e.cancel = true
          return
        }

        if (!e.event?.ctrlKey) {
          // Spostamento: la conferma è gestita da onAppointmentUpdating, invocato in modo sincrono subito dopo.
          // Il flag va scartato se l'update non parte (es. drop nella stessa cella).
          appointmentDragEndRef.current = true
          queueMicrotask(() => {
            appointmentDragEndRef.current = false
          })
          return
        }

        // Ctrl + trascinamento: duplicazione dell'evento nella posizione di destinazione, senza conferma
        e.cancel = true
        const startDate: Date = new Date(e.toItemData.startDate!)
        const endDate: Date = new Date(e.toItemData.endDate!)
        return client
          .post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/duplicate`, {
            appointmentId: e.toItemData.appointmentId,
            email: e.toItemData.email,
            startDate,
            endDate,
          })
          .then((response) => {
            notify(`Evento duplicato con successo.`, 'success', 2000)
            const newItem = {
              ...e.toItemData,
              appointmentId: response.data.id,
              startDate,
              endDate,
              teamsJoinUrl: response.data.onlineMeeting?.joinUrl,
            }
            const currentItems = schedulerRef.current?.instance().getDataSource().items() ?? []
            loadWithPending([...currentItems, newItem])
            return response
          })
          .catch((error) => {
            notify(`Errore duplicazione evento: ${error}`, 'error', 2000)
            return error
          })
      },
    }
  }, [client, loadWithPending, userInfo?.email, userInfo?.roles, users])

  const applySelectedMonthCell = useCallback(() => {
    const selectedDate = selectedMonthDateRef.current
    const instance = schedulerRef.current?.instance()
    if (!selectedDate || !instance || calendarStore.currentSchedulerView !== 'month') return

    const schedulerElement = instance.element()
    schedulerElement.querySelectorAll('.calendar-month-selected-cell').forEach((element) => {
      element.classList.remove('calendar-month-selected-cell')
    })

    const startViewDate = instance.getStartViewDate()
    const startDay = new Date(startViewDate.getFullYear(), startViewDate.getMonth(), startViewDate.getDate())
    const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())
    const dayIndex = Math.round((selectedDay.getTime() - startDay.getTime()) / 86400000)
    const cells = schedulerElement.querySelectorAll<HTMLElement>(
      '.dx-scheduler-work-space-month .dx-scheduler-date-table-cell',
    )

    cells[dayIndex]?.classList.add('calendar-month-selected-cell')
  }, [calendarStore.currentSchedulerView])

  const onContentReady = useCallback(() => {
    if (!schedulerRef.current) return
    const el = schedulerRef.current.instance().element()
    requestAnimationFrame(() => {
      const allDayRows = el.querySelectorAll<HTMLElement>(
        '.dx-scheduler-all-day-table-row, .dx-scheduler-all-day-title',
      )
      if (!allDayRows.length) return
      allDayRows.forEach((row) => {
        row.style.height = '120px'
      })
      applySelectedMonthCell()
    })
  }, [applySelectedMonthCell])

  const onCurrentViewChange = useCallback(
    (view: string) => calendarStore.setCurrentSchedulerView(view as SchedulerViewType),
    [],
  )

  const getIsoWeekNumber = useCallback((date: Date) => {
    const target = new Date(date)
    target.setHours(0, 0, 0, 0)
    target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7))
    const firstThursday = new Date(target.getFullYear(), 0, 4)
    firstThursday.setHours(0, 0, 0, 0)
    firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7))
    return 1 + Math.round((target.getTime() - firstThursday.getTime()) / 604800000)
  }, [])

  const customizeDateNavigatorText = useCallback(
    (info: { startDate: Date; endDate: Date; text: string }) => {
      if (calendarStore.currentSchedulerView !== 'week' && calendarStore.currentSchedulerView !== 'workWeek') {
        return info.text
      }

      return `${info.text} (Settimana ${getIsoWeekNumber(info.startDate)})`
    },
    [calendarStore.currentSchedulerView, getIsoWeekNumber],
  )

  const onCellClick = useCallback(
    (e: SchedulerTypes.CellClickEvent) => {
      if (calendarStore.currentSchedulerView !== 'month') return
      if (!e.cellData?.startDate) return

      const selectedDate = new Date(e.cellData.startDate)
      const selectedDayTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
      ).getTime()
      const now = Date.now()
      const previousClick = lastMonthCellClickRef.current
      const isDoubleClick = previousClick?.dayTime === selectedDayTime && now - previousClick.clickedAt <= 450

      lastMonthCellClickRef.current = isDoubleClick ? null : { dayTime: selectedDayTime, clickedAt: now }
      selectedMonthDateRef.current = selectedDate
      ;(e.cellElement as HTMLElement | undefined)?.classList.add('calendar-month-selected-cell')
      const instance = schedulerRef.current?.instance()
      const currentSchedulerDate = new Date((instance?.option('currentDate') as Date | string | number) ?? '')
      const isSameSelectedDay =
        currentSchedulerDate.getFullYear() === selectedDate.getFullYear() &&
        currentSchedulerDate.getMonth() === selectedDate.getMonth() &&
        currentSchedulerDate.getDate() === selectedDate.getDate()

      if (!isSameSelectedDay) {
        instance?.option('currentDate', selectedDate)
      }
      requestAnimationFrame(applySelectedMonthCell)

      if (isDoubleClick) {
        const { startDate, endDate } = getDefaultAppointmentRangeForDate(selectedDate)
        openEditor({ startDate, endDate, allDay: false }, true)
      }
    },
    [applySelectedMonthCell, calendarStore.currentSchedulerView, openEditor],
  )

  const onCellContextMenu = useCallback((e: SchedulerTypes.CellContextMenuEvent) => {
    e.event?.preventDefault()
    if (popupRef.current?.instance().option('visible')) return
    contextMenuDataRef.current = { type: 'cell', cellData: e.cellData }
    const instance = contextMenuRef.current?.instance()
    instance?.option('items', [{ id: 'new', text: 'Nuovo appuntamento', icon: 'plus' }])
    // Position at the click point: DevExtreme accepts the event as `of` at runtime (typings only list Element/Window).
    instance?.option('position', { my: 'left top', at: 'left top', of: e.event as unknown as Element })
    instance?.show()
  }, [])

  const onAppointmentContextMenu = useCallback(
    (e: SchedulerTypes.AppointmentContextMenuEvent) => {
      e.event?.preventDefault()
      if (popupRef.current?.instance().option('visible')) return
      schedulerRef.current?.instance().hideAppointmentTooltip()
      const appointmentData = (e.targetedAppointmentData ?? e.appointmentData) as ExtendedAppointment
      const canAct = canManageAppointment(
        userInfo?.roles,
        userInfo?.email,
        appointmentData.email,
        appointmentData.isOrganizer,
      )
      contextMenuDataRef.current = { type: 'appointment', appointmentData }
      const instance = contextMenuRef.current?.instance()
      instance?.option('items', [
        canAct ? { id: 'edit', text: 'Modifica', icon: 'edit' } : { id: 'view', text: 'Visualizza', icon: 'eyeopen' },
        {
          id: 'status',
          text: 'Disponibilità',
          icon: 'isblank',
          visible: canAct,
          items: [
            { id: 'status-Free', text: 'Disponibile', icon: appointmentData.status === 'Free' ? 'check' : undefined },
            {
              id: 'status-WorkingElsewhere',
              text: 'Altra postazione di lavoro',
              icon: appointmentData.status === 'WorkingElsewhere' ? 'check' : undefined,
            },
            {
              id: 'status-Tentative',
              text: 'Da confermare',
              icon: appointmentData.status === 'Tentative' ? 'check' : undefined,
            },
            { id: 'status-Busy', text: 'Occupato', icon: appointmentData.status === 'Busy' ? 'check' : undefined },
            { id: 'status-Oof', text: 'Fuori sede', icon: appointmentData.status === 'Oof' ? 'check' : undefined },
          ],
        },
        {
          id: 'convert',
          text: 'Converti',
          icon: 'rename',
          visible: canAct,
          items: APPOINTMENT_CONVERSIONS.map((conversion) => ({
            id: conversion.id,
            text: conversion.text,
            icon: conversion.icon,
          })),
        },
        { id: 'duplicate', text: 'Duplica', icon: 'copy', visible: canAct },
        { id: 'delete', text: 'Elimina', icon: 'trash', visible: canAct },
      ])
      // Position at the click point: DevExtreme accepts the event as `of` at runtime (typings only list Element/Window).
      instance?.option('position', { my: 'left top', at: 'left top', of: e.event as unknown as Element })
      instance?.show()
    },
    [userInfo?.email, userInfo?.roles],
  )

  // Aggiorna un sottoinsieme di campi di un appuntamento mantenendo invariato tutto il resto:
  // l'endpoint di update riscrive l'evento completo, quindi il payload riparte sempre dai dati correnti
  const applyAppointmentChanges = useCallback(
    async (
      appt: ExtendedAppointment,
      changes: Partial<ExtendedAppointment>,
      messages: { success: string; error: string },
    ) => {
      try {
        const updateValues = {
          ...appt,
          ...changes,
          appointmentId: appt.appointmentId,
          startDate: new Date(appt.startDate ?? ''),
          endDate: new Date(appt.endDate ?? ''),
          isOnlineMeeting: appt.isOnlineMeeting ?? false,
          requiredAttendees: expandAttendeesToPayload(appt.requiredAttendees, users),
          optionalAttendees: expandAttendeesToPayload(appt.optionalAttendees, users),
        }
        const formData = buildCalendarFormData({ values: updateValues })
        await client.put(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/update`, formData)
        notify(messages.success, 'success', 2000)
        const currentItems = schedulerRef.current?.instance().getDataSource().items() ?? []
        loadWithPending(
          currentItems.map((item: ExtendedAppointment) =>
            item.appointmentId === appt.appointmentId ? { ...item, ...changes } : item,
          ),
        )
      } catch (error) {
        notify(`${messages.error}: ${error}`, 'error', 2000)
      }
    },
    [client, loadWithPending, users],
  )

  const onContextMenuItemClick = useCallback(
    (e: ItemClickEvent) => {
      const data = contextMenuDataRef.current
      if (!data) return

      if (data.type === 'cell') {
        const cellStartDate = new Date(data.cellData.startDate)
        const { startDate, endDate } =
          calendarStore.currentSchedulerView === 'month'
            ? getDefaultAppointmentRangeForDate(cellStartDate)
            : {
                startDate: cellStartDate,
                endDate: data.cellData.allDay ? cellStartDate : new Date(cellStartDate.getTime() + 30 * 60 * 1000),
              }

        openEditor(
          {
            startDate,
            endDate,
            allDay: calendarStore.currentSchedulerView === 'month' ? false : (data.cellData.allDay ?? false),
          },
          true,
        )
        return
      }

      const appt = data.appointmentData
      const conversion = APPOINTMENT_CONVERSIONS.find((item) => item.id === e.itemData.id)

      if (conversion) {
        // Conversione: cambia l'oggetto, svuota le note e imposta la disponibilità, lasciando invariato il resto
        applyAppointmentChanges(
          appt,
          { text: conversion.text, description: '', status: conversion.status },
          { success: `Evento convertito in ${conversion.text}.`, error: 'Errore conversione evento' },
        )
      } else if (e.itemData.id === 'edit') {
        requestEditAppointment(appt)
      } else if (e.itemData.id === 'view') {
        openEditor(appt, false)
      } else if (e.itemData.id === 'delete') {
        requestDeleteAppointment(appt)
      } else if (e.itemData.id === 'duplicate') {
        dispatch({
          // La copia è sempre un evento proprio: l'eventuale flag di invito non va ereditato
          editData: buildEditData(appt, { appointmentId: undefined, isOrganizer: undefined, organizer: undefined }),
          creating: true,
        })
        pendingShowRef.current = true
      } else if (typeof e.itemData?.id === 'string' && e.itemData.id.startsWith('status-')) {
        applyAppointmentChanges(
          appt,
          { status: e.itemData.id.replace('status-', '') },
          {
            success: 'Disponibilità evento modificata con successo.',
            error: 'Errore aggiornamento disponibilità',
          },
        )
      }
    },
    [
      applyAppointmentChanges,
      calendarStore.currentSchedulerView,
      openEditor,
      requestDeleteAppointment,
      requestEditAppointment,
    ],
  )

  const handleDrawerSelectedUserIdsChange = useCallback(
    (selectedUserIds: string[]) => {
      const current = useCalendarStore.getState().selectedUserIds
      if (
        selectedUserIds.length === current.length &&
        selectedUserIds.every((value, index) => value === current[index])
      ) {
        return
      }
      calendarStore.setSelectedUserIds(selectedUserIds)
    },
    [calendarStore],
  )

  const handleMiniCalendarValueChange = useCallback((value: Date) => {
    schedulerRef.current?.instance().option('currentDate', value)
  }, [])

  const handleAppointmentLabelsToggle = useCallback(() => {
    setShowAppointmentLabels((currentValue) => {
      const nextValue = !currentValue
      localStorage.setItem('calendar-show-appointment-labels', String(nextValue))
      return nextValue
    })
  }, [])

  return (
    <React.Fragment>
      <div className={'content-block dx-card responsive-paddings'}>
        <div className="flex-container">
          <Toolbar>
            <ToolbarItem location="before" locateInMenu="never">
              <Button
                icon="menu"
                id="btn-menu-users"
                hint="Calendari"
                stylingMode="text"
                type="default"
                onClick={(e: ClickEvent) => {
                  if (drawerRef.current?.instance().option('opened')) drawerRef.current?.instance().hide()
                  else drawerRef.current?.instance().show()
                }}
              />
            </ToolbarItem>
            <ToolbarItem location="before" locateInMenu="never">
              <Button
                icon="refresh"
                hint="Aggiorna calendario"
                stylingMode="text"
                type="default"
                onClick={() => {
                  reloadCalendarFromServer()
                }}
              />
            </ToolbarItem>
            <ToolbarItem location="before" locateInMenu="never">
              <DropDownButton
                icon="clock"
                hint={
                  autoRefreshMinutes > 0
                    ? `Aggiornamento automatico: ogni ${autoRefreshMinutes} min`
                    : 'Aggiornamento automatico'
                }
                stylingMode="text"
                type={autoRefreshMinutes > 0 ? 'success' : 'default'}
                items={AUTO_REFRESH_OPTIONS}
                displayExpr="text"
                keyExpr="id"
                selectedItemKey={autoRefreshMinutes}
                onSelectionChanged={(e: any) => handleAutoRefreshChange(e.item.id)}
                showArrowIcon={false}
                dropDownOptions={{ width: 150 }}
              />
            </ToolbarItem>
            <ToolbarItem location="before" locateInMenu="never">
              <Button
                icon="tags"
                hint={showAppointmentLabels ? 'Nascondi etichette appuntamenti' : 'Mostra etichette appuntamenti'}
                stylingMode="text"
                type={showAppointmentLabels ? 'success' : 'default'}
                onClick={handleAppointmentLabelsToggle}
              />
            </ToolbarItem>
          </Toolbar>
          <Drawer
            id="calendar-drawer"
            defaultOpened={localStorage.getItem(`calendar-drawer-opened`) === 'true' ? true : false}
            ref={drawerRef}
            openedStateMode={'shrink'}
            position={'left'}
            revealMode={'slide'}
            render={() => (
              <CalendarPageDrawerContent
                miniCalendarRef={miniCalendarRef}
                currentDate={currentDate}
                displayedFlatCalendarsColored={displayedFlatCalendarsColored}
                selectedUserIds={calendarStore.selectedUserIds}
                onMiniCalendarValueChange={handleMiniCalendarValueChange}
                onSelectedUserIdsChange={handleDrawerSelectedUserIdsChange}
                onTreeReorder={handleTreeReorder}
                onGroupCreate={handleGroupCreate}
                onGroupRename={handleGroupRename}
                onGroupDelete={handleDeleteGroup}
              />
            )}
            onOpenedChange={(value: boolean) => {
              localStorage.setItem(`calendar-drawer-opened`, JSON.stringify(value))
            }}
          >
            <div id="content">
              <Scheduler
                ref={schedulerRef}
                timeZone="Europe/Rome"
                dataSource={schedulerDataSource}
                useDropDownViewSwitcher={false}
                defaultCurrentView={calendarStore.currentSchedulerView}
                defaultCurrentDate={currentDate}
                className="calendar-scheduler"
                startDayHour={0}
                endDayHour={24}
                remoteFiltering={true}
                dateSerializationFormat="yyyy-MM-ddTHH:mm:ssZ"
                textExpr="text"
                recurrenceRuleExpr=""
                recurrenceExceptionExpr=""
                descriptionExpr="description"
                allDayExpr="allDay"
                appointmentComponent={Appointment}
                appointmentTooltipComponent={AppointmentTooltip}
                onAppointmentFormOpening={onAppointmentFormOpening}
                onCurrentViewChange={onCurrentViewChange}
                customizeDateNavigatorText={customizeDateNavigatorText}
                onCurrentDateChange={(value: string | number | Date) => {
                  miniCalendarRef.current?.instance().option('value', value)
                }}
                onAppointmentUpdating={onAppointmentUpdating}
                onAppointmentDeleting={onAppointmentDeleting}
                appointmentDragging={appointmentDragging}
                maxAppointmentsPerCell={
                  calendarStore.currentSchedulerView === 'day' ||
                  calendarStore.currentSchedulerView === 'workWeek' ||
                  calendarStore.currentSchedulerView === 'week'
                    ? 'unlimited'
                    : 'auto'
                }
                onContentReady={onContentReady}
                onCellClick={onCellClick}
                onCellContextMenu={onCellContextMenu}
                onAppointmentContextMenu={onAppointmentContextMenu}
              >
                <Resource
                  label="Impiegato"
                  fieldExpr="email"
                  valueExpr={'email'}
                  displayExpr="fullname"
                  dataSource={users}
                  allowMultiple={false}
                  useColorAsDefault={true}
                />
                <SchedulerView type="day" />
                <SchedulerView type="week" />
                <SchedulerView type="workWeek" />
                <SchedulerView type="month" />
                <SchedulerToolbar>
                  <SchedulerToolbarItem
                    location="before"
                    locateInMenu="auto"
                    widget="dxButton"
                    options={{
                      icon: 'plus',
                      text: 'Nuovo appuntamento',
                      stylingMode: 'outlined',
                      type: 'normal',
                      onClick: onNewAppointmentButtonClick,
                    }}
                  />
                  <SchedulerToolbarItem name="today" location="before"></SchedulerToolbarItem>
                  <SchedulerToolbarItem name="dateNavigator" location="before" />
                  <SchedulerToolbarItem name="viewSwitcher" location="after" />
                </SchedulerToolbar>
              </Scheduler>
              <ContextMenu ref={contextMenuRef} onItemClick={onContextMenuItemClick} showEvent={{ name: '' }} />
              <Popup
                ref={popupRef}
                resizeEnabled={false}
                wrapperAttr={{ class: 'calendar-appointment-popup' }}
                width={
                  currentScreenSize.isXSmall
                    ? 'min(96vw, 350px)'
                    : currentScreenSize.isSmall
                      ? 'min(94vw, 560px)'
                      : 'min(92vw, 700px)'
                }
                height="auto"
                maxHeight="92vh"
                hideOnOutsideClick={false}
                showCloseButton={true}
                title={!state.creating ? state.editData.text : 'Nuovo evento'}
                toolbarItems={appointmentPopupToolbarItems}
                contentRender={appointmentPopupContent}
                deferRendering={true}
                onHidden={() => setAppointmentSaveDisabled(false)}
              />
              <RecurringScopeDialog
                open={scopeDialog.open}
                action={scopeDialog.action}
                onSelect={(scope) => {
                  const onSelect = scopeDialog.onSelect
                  setScopeDialog({ open: false, action: scopeDialog.action })
                  onSelect?.(scope)
                }}
                onCancel={() => {
                  const onSelect = scopeDialog.onSelect
                  setScopeDialog({ open: false, action: scopeDialog.action })
                  onSelect?.(null)
                }}
              />
            </div>
          </Drawer>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CalendarPage
